import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import type { Config, Widget } from './config';

type TemplateDesignerContextType = {
  config: Config;
  selectedWidgetId: string | null;
  selectedWidget: Widget | undefined;
  useSnapToGrid: boolean;
  moveStepSize: number;
  onClick: (widgetId: string) => void;
  updateWidget: (widget: Partial<Widget>) => void;
  addWidget: (widget: Widget) => void;
  isSelectedWidget: (widgetId: string) => boolean;
  onTransform: (isTransforming: boolean) => void;
  isTransformingRef: React.MutableRefObject<boolean>;
};

const TemplateDesignerContext = createContext<TemplateDesignerContextType | null>(null);

export const useTemplateDesignerContext = () => {
  const context = useContext(TemplateDesignerContext);
  if (!context) {
    throw new Error('useTemplateDesignerContext must be used within TemplateDesignerProvider');
  }
  return context;
};

type TemplateDesignerProviderProps = {
  children: React.ReactNode;
  config: Config;
  selectedWidgetId: string | null;
  selectedWidget: Widget | undefined;
  useSnapToGrid: boolean;
  moveStepSize: number;
  onClick: (widgetId: string) => void;
  updateWidget: (widget: Partial<Widget>) => void;
  addWidget: (widget: Widget) => void;
  onDragResizeChange?: (isTransforming: boolean) => void;
};

export const TemplateDesignerProvider = ({
  children,
  config,
  selectedWidgetId,
  selectedWidget,
  useSnapToGrid,
  moveStepSize,
  onClick,
  updateWidget,
  addWidget,
  onDragResizeChange,
}: TemplateDesignerProviderProps) => {
  const isTransformingRef = useRef(false);

  const onTransform = useCallback(
    (isTransforming: boolean) => {
      isTransformingRef.current = isTransforming;
      onDragResizeChange?.(isTransforming);
    },
    [onDragResizeChange],
  );

  const value = useMemo(
    () => ({
      config,
      selectedWidgetId,
      selectedWidget,
      useSnapToGrid,
      moveStepSize,
      onClick,
      updateWidget,
      addWidget,
      isSelectedWidget: (widgetId: string) => selectedWidgetId === widgetId,
      onTransform,
      isTransformingRef,
    }),
    [config, selectedWidgetId, selectedWidget, useSnapToGrid, moveStepSize, onClick, updateWidget, addWidget, onTransform],
  );

  return <TemplateDesignerContext.Provider value={value}>{children}</TemplateDesignerContext.Provider>;
};
